import React, { useState, useEffect, useRef, useCallback } from "react";

/**
 * This React component simulates a typewriter effect by displaying a message one character at a time
 * The message is typed out at a controlled speed, and once the typing is complete, it stops.
 *
 * Features:
 * - Simulates a typewriter effect by progressively revealing each character of a given message
 * - Handles typing in progress state to indicate when typing is happening
 * - Cleans up any pending timeouts when the component unmounts or when the message changes
 *
 * @param {string} message - The message to be typed out character by character
 * @returns {JSX.Element} A component that simulates a typewriter effect
 */
function TypingResponse({ message = "", setTypingInProgress }) {
  const [displayedText, setDisplayedText] = useState("");
  const typingTimeoutRef = useRef(null);

  const typeWriter = useCallback(() => {
    let index = 0;
    const maxLength = message?.length || 0;

    const write = () => {
      if (index <= maxLength) {
        setDisplayedText(message.slice(0, index));
        index++;
        typingTimeoutRef.current = setTimeout(write, 10);
      } else {
        setTypingInProgress(false);
      }
    };

    setTypingInProgress(true);
    write();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message]);

  useEffect(() => {
    if (!message) return;

    setDisplayedText("");
    clearTimeout(typingTimeoutRef.current);

    typeWriter();

    return () => {
      clearTimeout(typingTimeoutRef.current);
    };
  }, [message, typeWriter]);

  return (
    <span
      style={{
        fontSize: "1rem",
        display: "inline-block",
        wordWrap: "break-word",
        // Preserva as quebras de linha do backend enquanto digita. Sem isto o
        // corpo sai colapsado durante a animação e "salta" ao terminar.
        whiteSpace: "pre-wrap",
      }}
      // Mesmo slot que o ramo `read: true` de Answer.jsx — os dois são
      // exclusivos, nunca renderizam juntos.
      data-testid="answer-body"
    >
      {displayedText}
    </span>
  );
}

export default TypingResponse;
